/* global BigInt */
// Retrieve default currency from localStorage; fallback to USD
export const getDefaultCurrency = () => {
  try {
    const saved = localStorage.getItem('selectedPortfolioCurrency');
    return saved || 'USD';
  } catch (_) {
    return 'USD';
  }
};
/**
 * Format a value as PnL with fixed 2 decimals
 * @param {string} value - The value to format
 * @param {string} prefix - The prefix, $ by default
 * @returns {string} - Formatted currency string
 */
export const formatChangedAmount = (value, prefix = '$', showPlus = true) => {
  if (!value || value === 0 || value === "0.00" || value === "0") return '0';

  const numValue = typeof value === 'string' ? parseFloatFromString(value) : value;
  const roundedValue = Number(numValue.toFixed(2));
  const isNegative = roundedValue < 0;
  const plus = showPlus ? '+' : '';
  
  const formattedValue = Math.abs(roundedValue).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
  return isNegative ? `-${prefix}${formattedValue}` : `${plus}${prefix}${formattedValue}`;
};

/**
 * Format a PnL value using the current or provided currency symbol
 * @param {string|number} value - The value to format
 * @param {string} currency - Optional ISO currency code (defaults to stored portfolio currency)
 * @param {boolean} showPlus - Whether to show leading + for positives (defaults to true)
 * @returns {string}
 */
export const formatChangedCurrencyAmount = (value, currency, showPlus = true) => {
  const cur = currency || getDefaultCurrency();
  let symbol = '$';
  try {
    symbol = new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, minimumFractionDigits: 0, maximumFractionDigits: 0 })
      .formatToParts(0)
      .find(p => p.type === 'currency')?.value || '$';
  } catch (_) {
    symbol = '$';
  }
  return formatChangedAmount(value, symbol, showPlus);
};

/**
 * Format a value as currency
 * @param {string} value - The value to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value, currency) => {
  const cur = currency || getDefaultCurrency();
  if (value === undefined || value === null || value === '' || isNaN(parseFloatFromString(value))) return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(0);
  const numRaw = typeof value === 'string' ? parseFloatFromString(value) : value;
  // Round to cents to avoid displaying -$0.00 due to floating point noise
  let num = Math.round(numRaw * 100) / 100;
  if (Object.is(num, -0)) {
    num = 0;
  }
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(num);
  } catch (_) {
    // Fallback to $ if invalid code
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  }
};

/**
 * Format a value as a percentage
 * @param {string} value - The value to format
 * @returns {string} - Formatted percentage string
 */
export const formatPercentage = (value) => {
  const amount = formatChangedAmount(value, '', false);
  if (amount === '0') return amount;
  return `${amount}%`;
};

/**
 * Format a value as a percentage with a leading +
 * @param {string} value - The value to format
 * @returns {string} - Formatted percentage string
 */
export const formatChangedPercentage = (value) => {
  const amount = formatChangedAmount(value, '');
  if (amount === '0') return amount;
  return `${amount}%`;
};

/**
 * Format a number to a readable string with appropriate suffix (K, M, B)
 * @param {number} value - The numeric value to format
 * @returns {string} - Formatted string with suffix (e.g., "1.25B", "500K")
 */
export const formatNumberToReadable = (value) => {
  if (!value || value === 0) return '0';
  
  // Ensure we're working with a number
  const numValue = typeof value === 'string' ? parseFloatFromString(value) : value;
  
  if (numValue >= 1_000_000_000_000) {
    return `${(numValue / 1_000_000_000_000).toFixed(2)}T`;
  } else if (numValue >= 1_000_000_000) {
    return `${(numValue / 1_000_000_000).toFixed(2)}B`;
  } else if (numValue >= 1_000_000) {
    return `${(numValue / 1_000_000).toFixed(2)}M`;
  } else if (numValue >= 1_000) {
    return `${(numValue / 1_000).toFixed(2)}K`;
  } else {
    return numValue.toFixed(2);
  }
};

/**
 * Format a value as human-readable currency using suffixes (K, M, B, T)
 * @param {number|string} value - The value to format; accepts raw numbers or strings like "$1,234,567" or "1.2B"
 * @returns {string} - Formatted currency string like "$1.25B"
 */
export const formatCurrencyReadable = (value, currency) => {
  const cur = currency || getDefaultCurrency();
  // Normalize to a number, handling inputs with $ sign and K/M/B/T suffixes
  const numericValue = typeof value === 'number' ? value : parseReadableNumberToFloat(value);
  if (!numericValue || numericValue === 0) return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(0);
  // Build with suffix while preserving symbol for readability
  const short = formatNumberToReadable(numericValue);
  const symbol = new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, minimumFractionDigits: 0, maximumFractionDigits: 0 }).formatToParts(0).find(p => p.type === 'currency')?.value || '$';
  return `${symbol}${short}`;
};


export const decimalToScaledBigInt = (value, scale = 18) => {
  if (value === null || value === undefined || value === '') return 0n;
  let str = normalizeDecimalInput(String(value).trim().replace(/%/g, '')).replace(/^\+/, '');
  if (!str || str === '-') return 0n;

  let sign = 1n;
  if (str.startsWith('-')) {
    sign = -1n;
    str = str.slice(1);
  }

  // API decimal strings should not use exponent notation, but tolerate numeric fallbacks.
  if (/e/i.test(str)) {
    const asNumber = Number(str);
    if (!Number.isFinite(asNumber)) return 0n;
    str = Math.abs(asNumber).toFixed(scale);
  }

  const [wholeRaw = '0', fracRaw = ''] = str.split('.');
  const whole = (wholeRaw.replace(/\D/g, '') || '0');
  const frac = (fracRaw.replace(/\D/g, '').padEnd(scale, '0')).slice(0, scale);
  return sign * (BigInt(whole) * (10n ** BigInt(scale)) + BigInt(frac || '0'));
};

export const scaledBigIntToDecimalString = (value, scale = 18) => {
  let amount = BigInt(value || 0);
  if (amount === 0n) return '0';
  const sign = amount < 0n ? '-' : '';
  if (amount < 0n) amount = -amount;
  const divisor = 10n ** BigInt(scale);
  const whole = amount / divisor;
  const frac = String(amount % divisor).padStart(scale, '0').replace(/0+$/, '');
  return `${sign}${whole.toString()}${frac ? `.${frac}` : ''}`;
};

export const addDecimalStrings = (values = [], scale = 18) => {
  const total = values.reduce(
    (sum, item) => sum + decimalToScaledBigInt(item, scale),
    0n
  );
  return scaledBigIntToDecimalString(total, scale);
};

export const subtractDecimalStrings = (value, subtractValues = [], scale = 18) => {
  const total = subtractValues.reduce(
    (sum, item) => sum + decimalToScaledBigInt(item, scale),
    0n
  );
  return scaledBigIntToDecimalString(decimalToScaledBigInt(value, scale) - total, scale);
};

export const compareDecimalStrings = (left, right, scale = 18) => {
  const leftValue = decimalToScaledBigInt(left, scale);
  const rightValue = decimalToScaledBigInt(right, scale);
  if (leftValue === rightValue) return 0;
  return leftValue > rightValue ? 1 : -1;
};

/**
 * Format a price with dynamic precision based on magnitude
 * @param {number|string} price - The price to format
 * @returns {string} - Formatted price string with $ symbol and appropriate precision
 */
export const formatPrice = (price, currency) => {
  const cur = currency || getDefaultCurrency();
  const num = typeof price === 'string' ? parseFloatFromString(price) : price;
  
  if (num === null || num === undefined || num === 0) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(0);
  }
  
  // Determine the decimal precision based on price magnitude
  const absVal = Math.abs(num);
  let digits = 2;
  
  if (absVal < 0.000001) {
    digits = 10;
  } else if (absVal < 0.0001) {
    digits = 8;
  } else if (absVal < 0.01) {
    digits = 6;
  } else if (absVal < 1) {
    digits = 4;
  }
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(num);
  } catch (_) {
    // Fallback to USD if invalid currency code
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(num);
  }
};


/**
 * Format a price with dynamic precision based on magnitude
 * @param {number|string} price - The amount to format
 * @param {string} prefix - The prefix, empty by default
 * @returns {string} - Formatted price string with $ symbol and appropriate precision
 */
export const formatAmount = (amount, prefix = '') => {
  if (amount === null || amount === undefined) return `${prefix}0`;
  
  // Convert to number if it's a string
  const numValue = typeof amount === 'string' ? parseFloatFromString(amount) : amount;
  
  // Return early for zero
  if (numValue === 0) return `${prefix}0`;
  
  // Determine the decimal precision based on price magnitude
  let precision = 2; // Default precision for values >= 1

  const absVal = Math.abs(numValue);
  if (absVal < 0.00000001) {
    precision = 18;
  } else if (absVal < 10) {
    precision = 8;
  }
  
  // Format the value with appropriate precision
  const res = `${prefix}${numValue.toFixed(precision)}`;

  // Strip right 0's
  return res.replace(/\.?0+$/, '');
};

export const normalizeDecimalInput = (value) => {
  if (value === null || value === undefined) return value;

  const original = String(value);
  let body = original.trim();
  if (!body) return body;

  body = body.replace(/^[$€£¥₹]+|[$€£¥₹]+$/g, '').trim();
  body = body.replace(/[\s\u00a0'_]/g, '');

  let sign = '';
  if (body.startsWith('+') || body.startsWith('-')) {
    sign = body[0];
    body = body.slice(1);
  }
  if (!body || /[+-]/.test(body)) return original.trim();

  const commaCount = (body.match(/,/g) || []).length;
  const dotCount = (body.match(/\./g) || []).length;
  let normalized = body;

  if (commaCount === 0 && dotCount === 0) {
    normalized = body;
  } else if (commaCount === 0) {
    normalized = dotCount > 1 ? body.replace(/\./g, '') : body;
  } else if (dotCount === 0) {
    normalized = commaCount === 1 ? body.replace(',', '.') : body.replace(/,/g, '');
  } else {
    const lastComma = body.lastIndexOf(',');
    const lastDot = body.lastIndexOf('.');
    if (lastComma > lastDot) {
      normalized = body.slice(0, lastComma).replace(/\./g, '') + '.' + body.slice(lastComma + 1);
    } else {
      normalized = body.slice(0, lastDot).replace(/,/g, '') + '.' + body.slice(lastDot + 1);
    }
  }

  return `${sign}${normalized}`;
};

/**
 * Parse a float from a string, handling commas and other formatting
 * @param {string} value - The string to parse
 * @returns {number} - The parsed float value
 */
export const parseFloatFromString = (value) => {
  if (value === null || value === undefined) return 0;
  
  // If it's already a number, return it directly without conversion
  if (typeof value === 'number') return value;
  
  // Convert to string if it's not already
  const str = String(value);
  
  // Remove commas, '+' signs, etc.
  const cleanValue = normalizeDecimalInput(str.replace(/%/g, '')).replace(/^\+/, '');
  
  // Parse as float, using Number() instead of parseFloat() to preserve precision
  // Number() maintains the full precision while parseFloat() might truncate
  const result = Number(cleanValue);
  
  // Return 0 if the result is NaN
  return isNaN(result) ? 0 : result;
};

/**
 * Parse a readable market cap string (like "1.2B" or "500M") into a float
 * @param {string} value - The string to parse (e.g., "1.2B", "500M", "10K")
 * @returns {number} - The parsed value as a float
 */
export const parseReadableNumberToFloat = (value) => {
  if (!value || typeof value !== 'string') return 0;
  
  // Match patterns like "1.2B", "500M", "10K"
  const match = value.match(/^[$]?([\d,]+\.?\d*)([TBMK])?$/i);
  
  if (!match) {
    // If no special suffix, just parse it normally
    return parseFloatFromString(value);
  }
  
  // Get the number part and the suffix
  const numPart = parseFloatFromString(match[1]);
  const suffix = match[2]?.toUpperCase();
  
  // Apply multiplier based on suffix
  if (suffix === 'T') {
    return numPart * 1_000_000_000_000; // Trillions
  } else if (suffix === 'B') {
    return numPart * 1_000_000_000; // Billions
  } else if (suffix === 'M') {
    return numPart * 1_000_000; // Millions
  } else if (suffix === 'K') {
    return numPart * 1_000; // Thousands
  }
  
  return numPart;
};

/**
 * Get CSS class based on value change (positive/negative)
 * @param {string|number} value - The value to check
 * @returns {string} - CSS class name
 */
export const getChangeColorClass = (value) => {
  const numRaw = parseFloatFromString(value);
  // Round to 2 decimals to align with displayed currency and avoid -0 edge cases
  let numValue = Math.round(numRaw * 100) / 100;
  if (Object.is(numValue, -0)) {
    numValue = 0;
  }
  if (numValue > 0) return 'text-green-600 dark:text-green-400';
  if (numValue < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-600 dark:text-gray-400';
};

/**
 * Get CSS class based on realized percentage
 * @param {number} percentage - The percentage value (0-100+)
 * @returns {string} - CSS class name
 */
export const getRealizedPercentageColorClass = (percentage) => {
  if (percentage >= 100) return 'text-green-600 dark:text-green-400';
  if (percentage >= 75) return 'text-green-500 dark:text-green-300';
  if (percentage >= 50) return 'text-yellow-600 dark:text-yellow-400';
  if (percentage >= 25) return 'text-yellow-500 dark:text-yellow-300';
  return 'text-red-500 dark:text-red-400';
};

/**
 * Format position age as human-readable relative time
 * @param {string} isoTimestamp - ISO 8601 timestamp of oldest unsold lot
 * @returns {string} - Human-readable duration (e.g., "3 months", "1 year")
 */
export const formatPositionAge = (isoTimestamp) => {
  if (!isoTimestamp) return null;
  
  const ageDate = new Date(isoTimestamp);
  if (isNaN(ageDate.getTime())) return null;
  
  const now = new Date();
  const diffMs = now - ageDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays < 1) return 'today';
  if (diffDays === 1) return '1 day';
  if (diffDays < 7) return `${diffDays} days`;
  
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) return '1 week';
  if (diffWeeks < 4) return `${diffWeeks} weeks`;
  
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month';
  if (diffMonths < 12) return `${diffMonths} months`;
  
  const diffYears = Math.floor(diffDays / 365);
  const remainingMonths = Math.floor((diffDays % 365) / 30);
  
  if (diffYears === 1 && remainingMonths === 0) return '1 year';
  if (diffYears === 1) return `1 year, ${remainingMonths} months`;
  if (remainingMonths === 0) return `${diffYears} years`;
  return `${diffYears} years, ${remainingMonths} months`;
}; 